import { FieldTemplateProps } from "@rjsf/utils";
import { useFormSchema } from "src/api/hooks/useFormSchema";
import { useState } from "react";

/**
 * Custom FieldTemplate to control how each field (label + widget + errors) is rendered
 * This wraps the entire field including the label, widget, description, and errors
 */
export const CustomFieldTemplate = (props: FieldTemplateProps) => {
  const { data: schema } = useFormSchema();
  const {
    id,
    classNames,
    style,
    label,
    help,
    required,
    description,
    errors,
    rawErrors,
    children,
    schema: fieldSchema,
  } = props;

  const [showDescription, setShowDescription] = useState(false);
  const primaryColor = schema?.theme?.colors?.primary ?? "PrimaryGreen";

  // Get the raw description string to allow HTML rendering (e.g., <br>, <b> tags)
  // Prefer the raw schema description if description is a ReactNode
  const descriptionString =
    typeof description === "string"
      ? description
      : (fieldSchema?.description as string | undefined) || "";

  return (
    <div
      id={`${id}-template`}
      className={`flex flex-col w-full max-w-full box-border ${
        classNames || ""
      }`}
    >
      {/* Label */}
      {label && id !== "root" && (
        <label
          htmlFor={id}
          className="text-base field-label font-medium text-Grey7 flex items-center justify-between relative"
        >
          <span className="flex items-center gap-1 secondary-text">
            {label}
            {required && (
              <span
                className="text-PrimaryRed ml-1 secondary-text"
                aria-label="required"
              >
                *
              </span>
            )}
          </span>
          {description && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowDescription(!showDescription);
              }}
              className="description-toggle-btn flex-shrink-0"
              aria-label={
                showDescription ? "Hide description" : "Show description"
              }
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "2px 4px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s ease",
                borderRadius: "50%",
                color: primaryColor || "#4CAF50",
                marginLeft: "8px",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.05)";
                e.currentTarget.style.transform = "scale(1.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
                e.currentTarget.style.transform = "scale(1)";
              }}
            >
              <i
                className="fa-solid fa-circle-info"
                style={{
                  fontSize: "12px",
                  opacity: showDescription ? 1 : 0.6,
                }}
              />
            </button>
          )}
        </label>
      )}

      {/* Description - toggleable */}
      {description && showDescription && (
        <div
          className="description-content"
          style={{
            fontSize: "13px",
            color: "#666",
            fontStyle: "italic",
            marginTop: "4px",
            marginBottom: "4px",
            padding: "6px 10px",
            backgroundColor: "rgba(0, 0, 0, 0.02)",
            borderRadius: "4px",
            borderLeft: `2px solid ${primaryColor || "#4CAF50"}`,
            animation: "fadeIn 0.3s ease-in",
          }}
          dangerouslySetInnerHTML={{ __html: descriptionString }}
        />
      )}

      {/* Widget (input/select/etc) */}
      <div className="w-full">{children}</div>

      {/* Errors - RJSF provides errors as a ReactNode */}
      {errors && <div className="text-sm text-red-500 mt-1">{errors}</div>}

      {/* Help text */}
      {help && <div className="text-sm text-Grey5 mt-1">{help}</div>}
    </div>
  );
};
